import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { snapOrFromTo, snapOrTo } from 'animations/snapTween';
import {
  BOARD_MIN_SCALE_FACTOR,
  BOARD_PORTRAIT_RATIO,
  SMALL_CARD_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableSnap } from 'hooks/useTableSnap';
import * as _ from 'lodash';
import { ZIndex } from 'styles';

import { mapToCard } from 'components/Card';
import CommunityCard from 'components/Card/CommunityCard';

import { useBoardDeficit } from './boardDeficit';
import BoardMissingNotice, { missingSlotClassName } from './BoardMissingNotice';
import { boardCardGapPx, boardHalfWidthAtDesignSize } from './boardScale';

// The run keys the broadcaster may emit (run-it-twice caps at 3 runs).
const RUN_KEYS = ['run1', 'run2', 'run3'] as const;

// One branch row's height on top of the card itself: the card wrap's 4px padding
// + 2px accent border on each side, plus BRANCH_GAP of row padding.
const BRANCH_ROW_EXTRA = 22;

/**
 * The height of one SETTLED branch row — a card plus that trim.
 *
 * A settled board is the SAME SIZE as the live one; only its dimming marks it as
 * spent. It used to draw at 80% as well, which is removed (AC-7.2 invents no new
 * visual treatments), so every branch row is now this height and the name only
 * distinguishes which rows the Pot counts, not a different size.
 *
 * Exported because the Pot counts these rows to put the main-pot pill on the
 * live board (Pot.tsx::multiRunRowOffset). Only settled rows matter to that sum:
 * exactly one run is live, so every row above or below it is a settled one, and
 * the live row's own height cancels out of the offset.
 */
// Plain px, and NOT `withViewScale`. This measures the card, and since the board
// rework the card paints at its DESIGN size (CommunityCard's BaseStyle,
// boardSlotStyle below) with the board wrapper's single `scale()` doing all the
// sizing. A row height still tracking `--table-view-scale` would drift from the
// boards it is counting at any Table View Size but the default.
export const SETTLED_BRANCH_ROW_HEIGHT = `calc(${SMALL_CARD_HEIGHT}px + ${BRANCH_ROW_EXTRA}px)`;

/**
 * How much of the felt oval's HEIGHT a run-it-twice fork may occupy.
 *
 * The seats sit ON the oval's rim, so the clear band down the middle is the
 * oval minus a seat (SEAT_HEIGHT plus its name/stack pills) at the top and the
 * bottom. This is the calibration knob: raise it for bigger cards, lower it if
 * a three-run stack still crowds the seats on some ring size.
 */
const FORK_FELT_HEIGHT_BUDGET = 0.62;

/**
 * Absolute floor on the fitted scale.
 *
 * NOT a legibility floor — the budget already tracks the felt, so a small felt
 * is meant to draw a small fork; a floor that fought it would just put the
 * cards back on the seats, which is the bug. This is a guard against a felt
 * height of ZERO: `--felt-oval-height` is published from a measured container,
 * and `useContainerSize` seeds at 0x0 and measures in an effect, so every table
 * mount renders one frame at 0. A CSS `var()` fallback does not cover that —
 * the var IS set, to 0 — and without this the board would blink out for a frame.
 */
const FORK_SCALE_MIN = 0.35;

/**
 * Ceiling on the fitted scale, as a multiple of the single-board scale. Stops a
 * two-run fork ballooning past the size the rest of the felt is drawn at just
 * because it has the vertical room to spare.
 */
const FORK_SCALE_MAX = 1.3;

/**
 * Landscape felt-oval height at design size — the `--felt-oval-height` fallback.
 * Container is 1400x700 at fit 1 and tableOvalStyle insets it 20% top and bottom.
 */
const DESIGN_FELT_OVAL_HEIGHT = 420;

/**
 * The board's painted scale, as a CSS expression.
 *
 * `--board-scale` is the real answer and comes from the table root
 * (Table/index.tsx -> resolveBoardScale): the felt's scale, floored for
 * legibility, then BOUNDED by the room the flank seats actually leave. That
 * bound cannot be expressed here — it needs the measured container width and
 * the seat ring, and this component sees neither.
 *
 * The fallback is the unbounded expression, for a board mounted outside a table
 * (stories, unit tests) and for the single frame before the container is
 * measured. It is deliberately the OLD behaviour rather than a constant: out of
 * a table there are no seats to be covered by.
 *
 * The fork's ceiling is derived from that same base, so a fork inherits the
 * seat bound too — the felt-height budget below may only make it SMALLER.
 *
 * ponytail: that inherited bound is computed for a ONE-ROW board
 * (maxBoardScaleForSeats uses boardHalfHeightAtDesignSize), so it charges the
 * board only against seats level with a single row. A fork is up to three rows
 * tall and reaches seats higher and lower on the ring than the bound counted.
 * It is still strictly better than the no-bound behaviour it replaces, and the
 * felt-height budget below already caps the fork vertically. Upgrade path: pass
 * the fork's row count into resolveBoardScale so the level-seat test uses the
 * stack's real half-height — worth doing if a three-run landscape fork is ever
 * reported touching a flank seat.
 *
 * A LANDSCAPE fork (two or three runs) is sized to the felt instead: the stack
 * grows a row per run while the landscape oval is only 60% of the container's
 * height, so three runs at the single-board scale covered the whole felt and
 * ran into the seats. Dividing the height budget by the stack cuts three runs
 * down to fit AND lets two runs paint larger than a single board, which is the
 * whole point of spending the room. Portrait keeps the single-board scale
 * untouched: its oval is 80% of a much taller container, so a fork already fits.
 *
 * ONE expression, used by the transform, by the negative margin that reclaims
 * its layout slack, and by forkRowOffset. Those disagreeing is a whole class of
 * bug — a footprint out of step with the visuals opens a gap under the felt,
 * and an offset out of step with the boards it counts drifts the pot pill and
 * the winner banner off their row.
 */
export const boardPaintScale = (isPortrait: boolean, forkRows = 0): string => {
  const base = `var(--board-scale, calc(max(${BOARD_MIN_SCALE_FACTOR}, var(--seat-scale, 1)) * ${
    isPortrait ? BOARD_PORTRAIT_RATIO : 1
  }))`;
  if (isPortrait || forkRows < 2) return base;
  const stackPx = forkRows * (SMALL_CARD_HEIGHT + BRANCH_ROW_EXTRA);
  return `clamp(${FORK_SCALE_MIN}, calc(${FORK_FELT_HEIGHT_BUDGET} * var(--felt-oval-height, ${DESIGN_FELT_OVAL_HEIGHT}) / ${stackPx}), calc(${base} * ${FORK_SCALE_MAX}))`;
};

/**
 * A CSS length for a shift of `rows` branch rows away from the fork's centre.
 *
 * Times the board's painted scale because the fork is drawn scaled and these
 * are board pixels, not the pixels of whatever rides along. `''` when there is
 * nothing to shift, so callers keep their un-forked anchor untouched.
 */
export const forkRowOffset = (rows: number, isPortrait: boolean, forkRows: number): string =>
  rows === 0
    ? ''
    : `calc(${rows} * (${SETTLED_BRANCH_ROW_HEIGHT}) * ${boardPaintScale(isPortrait, forkRows)})`;

/**
 * Which branch row the winning-hand banner sits on — the run key, not an
 * offset: the banner measures that row's own box (see TableWinnerMessage).
 *
 * The banner names the hand that just took the board being run NOW, so it must
 * not cover that board — and it must land in the SAME place in portrait and
 * landscape, which it did not: portrait anchored below the fork and landscape
 * above it, so one 3-run hand drew the banner over run 3 on a phone and over
 * the live run 1 on a desktop (EN-663).
 *
 * The rule, in both orientations: ride run 1's row, or the last run's row when
 * run 1 is itself the board being run now. Two spots, never the live one.
 *
 * `''` for anything that is not a fork (one board, or no boards on the felt).
 */
export const winnerBannerRun = (runKeys: string[], currentRun: number): string => {
  if (runKeys.length < 2) return '';
  return runKeys[0] === `run${currentRun}` ? runKeys[runKeys.length - 1] : runKeys[0];
};

/**
 * The branch rows the fork draws — every run the hand WILL have, not just the
 * ones dealt so far.
 *
 * The broadcaster seeds the in-progress run from the live board and back-fills
 * prior runs from usedCards, so an empty/absent run means it hasn't been dealt
 * yet. Those runs still get a row: `runs` names the agreed count up front, and
 * the whole fork is sized off this list (boardPaintScale, forkRowOffset, the
 * pot pill's row, the banner's row). Counting only the dealt runs re-laid the
 * felt out as each board landed — one board, then two, then three — so every
 * card resized twice mid-run-out. An undealt row paints nothing but its trunk
 * connector, so the space is simply held.
 *
 * Without `runs` (or with an unparseable one) it falls back to the dealt runs,
 * and a dealt run is never dropped however odd the count reads — a board on the
 * felt with no row to sit on would vanish.
 */
export const getRunKeys = (multiRunCards: any, runs?: unknown): string[] => {
  const dealt = RUN_KEYS.filter(
    (k) => Array.isArray(multiRunCards?.[k]) && multiRunCards[k].some((c: string) => !!c)
  );
  if (dealt.length === 0) return [...dealt];
  const total = Math.min(Number(String(runs ?? '').split(',')[0]) || 0, RUN_KEYS.length);
  return RUN_KEYS.filter((k, i) => i < total || dealt.includes(k));
};

/**
 * Whether a hand-number transition should sweep the community board off the felt.
 *
 * `hand === 14` (PAY_WINNERS) fires for BOTH an all-fold win AND a real
 * showdown, so it must NOT sweep unconditionally: at a showdown the board has to
 * stay on the felt through the winner reveal (13 → 14) and only clear at the
 * inter-hand processing (`hand === 15`, hidden via `isProcessing`) / the
 * next-hand reset. Sweeping at every 14 wiped the board mid-showdown while the
 * winning hand was still being shown (the reported bug, both orientations).
 * Only the all-fold case (`GAME_MSG_ALLFOLD`) sweeps at 14; the new-hand markers
 * (0 / '' / '1') always sweep. Pure so the showdown-vs-all-fold gate is
 * unit-testable.
 */
export const shouldSweepBoard = (currNum: number, hand: string, isAllFold: boolean): boolean =>
  currNum === 0 || (currNum === 14 && isAllFold) || hand === '' || hand === '1';

/** One seat's claim on a run-it-twice board. */
export type RunWinner = { seat: number; name?: string; win: number };

/**
 * The seats that took one run-it-twice board, with the name and amount to show.
 *
 * `details` (written by predistpot) is authoritative: it holds only the seats
 * that actually won this board. `runWinners.winners` cannot be trusted on its
 * own — it also carries the losing "side"-typed seat, which would credit and
 * highlight the wrong player — so the fallback pairs it with `winTypes` and
 * resolves the name from the seat. The fallback only matters for payloads that
 * predate the durable `details` field.
 */
export const runWinnerList = (runData: any, players?: any[]): RunWinner[] => {
  const details = Array.isArray(runData?.details) ? runData.details : [];
  if (details.length > 0) {
    return details.map((d: any) => ({ seat: +d.seat, name: d.name, win: +d.win }));
  }
  const winTypes = runData?.winTypes ?? [];
  return (runData?.winners ?? [])
    .map((w: any, i: number) => ({ seat: +w, i }))
    .filter((w: any) => winTypes[w.i] === 'win')
    .map((w: any) => ({
      seat: w.seat,
      name: players?.[w.seat]?.displayName,
      win: NaN
    }));
};

/**
 * Whether this seat took the board — drives the green branch highlight.
 *
 * The seat is coerced because the winner seats come off the wire (`details[].seat`
 * can arrive as a string) while `currentPlayer.seatId` is a number; a strict
 * `includes` between the two silently never matches, leaving the winner's own
 * board unhighlighted. Seat 0 is the unseated/observer sentinel, never a winner.
 */
export const seatWonRun = (winnerList: RunWinner[], seatId: unknown): boolean => {
  const seat = Number(seatId);
  if (!Number.isFinite(seat) || seat <= 0) return false;
  return winnerList.some((w) => Number(w.seat) === seat);
};

/**
 * Animated reveal for each community card — slides up with scale bounce.
 */
const CardReveal = ({ children, index }: { children: React.ReactNode; index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const shouldSnap = useTableSnap();
  const isHandHistorySnapshot = useHandHistorySnapshotMode();

  useEffect(() => {
    if (!ref.current) return;
    if (isHandHistorySnapshot) {
      gsap.set(ref.current, { y: 0, opacity: 1, scale: 1 });
      return;
    }
    // Capture the tween so we can kill it on unmount — without this, an
    // in-flight reveal whose card unmounts (hand reset mid-reveal) leaves
    // GSAP holding a reference to a detached node, which adds up across
    // hands and contributes to the full-GC pauses we saw in the trace.
    // While the table is snapping (arrival after a switch) the board is
    // already on the felt, so the card is placed at its final position
    // instead of being revealed again.
    const tween = snapOrFromTo(
      shouldSnap(),
      ref.current,
      { y: 20, opacity: 0, scale: 0.7 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.4,
        delay: (index % 3) * 0.08, // stagger within flop
        ease: 'back.out(1.7)'
      }
    );
    return () => {
      tween?.kill();
    };
    // shouldSnap is read at mount only — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandHistorySnapshot]);

  return (
    <div ref={ref} style={cardOverlayStyle}>
      {children}
    </div>
  );
};

const HoldEmCards = () => {
  const shouldSnap = useTableSnap();
  const hand = useTableSelector((table) => table.game.hand);
  const languageKey = useTableSelector((table) => table.game.languageKey);
  const game = useTableSelector((table) => table.game);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const cardGap = boardCardGapPx(isPortrait);

  // Half the board's reserved (unscaled) size — used to reclaim the layout slack
  // a scale transform leaves behind (it shrinks pixels, not the layout box).
  // Taken from boardScale.ts, which bounds the board off the same half-width:
  // the reach and the footprint have to be the same board.
  const boardHalfWidth = boardHalfWidthAtDesignSize(isPortrait);

  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  const playerGUID = useTableSelector((table) => table.currentPlayer.playerGUID);
  const multiRunCards = useTableSelector((table) => table.game.multiRunCards);

  // Only take over the board with stacked run-outs once the broadcaster has
  // actually sent per-run boards. Until then (or if the feature isn't wired on
  // the backend) fall back to the normal single-board render so nothing breaks.
  const runKeys = getRunKeys(multiRunCards, game.runs);
  const showMultiRun = isMultiRunActive && runKeys.length > 0;

  // See boardPaintScale: a landscape fork is sized to the felt, everything else
  // to the seats. Same expression the pot pill and the winner banner count in.
  const boardScale = boardPaintScale(isPortrait, showMultiRun ? runKeys.length : 0);

  // Half the board's reserved (unscaled) HEIGHT. A fork reserves one branch row
  // per run, not one card — reclaiming a single card's slack under a shrunken
  // three-run stack left ~50px of dead space above and below it, which pushed
  // the felt details and the pot down the column.
  const boardHalfHeight = showMultiRun
    ? (runKeys.length * (SMALL_CARD_HEIGHT + BRANCH_ROW_EXTRA)) / 2
    : SMALL_CARD_HEIGHT / 2;

  const holdemRef = useRef<HTMLDivElement>(null);
  const prevHandRef = useRef(hand);
  const [isVisible, setIsVisible] = useState(true);
  const [prevCards, setPrevCards] = useState<any[]>([]);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  // Fingerprint of the last swept board — suppresses the same card set from
  // reappearing at hand='6' of the NEW hand, because the engine doesn't clear
  // card1..card5 on table reset (they linger in Redux until new cards are dealt).
  const sweptFingerprintRef = useRef('');
  const currentFingerprintRef = useRef('');

  // Kill the untracked board tweens (position nudge, sweep-out, re-show) on
  // unmount so a board that unmounts mid-animation doesn't orphan them against
  // detached nodes. Per-card CardReveal tweens already self-kill on unmount.
  useEffect(() => {
    const board = holdemRef.current;
    const row = cardsContainerRef.current;
    return () => {
      if (board) gsap.killTweensOf(board);
      if (row) gsap.killTweensOf(row);
    };
  }, []);

  useEffect(() => {
    if (!holdemRef.current) return;
    if (isHandHistorySnapshot) {
      gsap.set(holdemRef.current, { y: 0 });
      return;
    }
    // Keep the board at y:0 so it sits tight against the table details beneath
    // it (the old -25 landscape lift was for the previous side-by-side layout).
    gsap.to(holdemRef.current, {
      y: 0,
      duration: 0.5,
      ease: 'power2.out'
    });
  }, [isHandHistorySnapshot, isMultiRunActive, isPortrait]);

  // Track the highest dealing stage seen during this hand
  // so we don't show cards that were never actually dealt on the table
  const handNum = +hand;
  const maxStageRef = useRef(-1);
  const isAllFold = languageKey === 'GAME_MSG_ALLFOLD';
  const isProcessing = languageKey === 'GAME_MSG_PROCESSING';

  const boardStageFromCards = () => {
    if (game.card5) return 2;
    if (game.card4) return 1;
    if (game.card1 && game.card2 && game.card3) return 0;
    return -1;
  };

  // A hand-number DECREASE is a new hand, full stop. The old reset fired only
  // on a render where handNum <= 1, but hands 0-1 are a sliver of the cycle: a
  // client that misses that window (backgrounded, lobby-browsing, kept-alive
  // carousel panel) used to carry the previous hand's stage into the next
  // hand's pre-flop and render its masked 'facedown' slots — the five-HJ-backs
  // bug (docs/investigations/20260821-community-cards-facedown-after-resume.md).
  const lastRenderHandNumRef = useRef(handNum);
  const isNewHandBoundary = handNum <= 1 || (handNum < lastRenderHandNumRef.current && handNum < 6);
  lastRenderHandNumRef.current = handNum;

  if (isNewHandBoundary) {
    maxStageRef.current = -1;
  }
  // Update max stage based on dealing progress (not end-of-hand states)
  if (handNum >= 6 && handNum < 13) {
    if (handNum >= 10) maxStageRef.current = Math.max(maxStageRef.current, 2);
    else if (handNum >= 8) maxStageRef.current = Math.max(maxStageRef.current, 1);
    else if (handNum >= 6) maxStageRef.current = Math.max(maxStageRef.current, 0);
  }
  // Coalesced websocket bursts can drop the intermediate river-stage render:
  // e.g. turn board is visible, then the queued river payload and showdown
  // payload collapse to only hand='13'. A real showdown still carries the board
  // cards, so catch up from card1..card5. Keep all-fold/processing excluded so
  // the old-card values the engine leaves in Redux do not reappear between hands.
  if (handNum >= 13 && !isAllFold && !isProcessing) {
    maxStageRef.current = Math.max(maxStageRef.current, boardStageFromCards());
  }

  const maxStage = maxStageRef.current;

  // Build community cards — only up to the stage we've actually seen dealt
  const cardEntries: { card: string; stage: number }[] = [];
  if (maxStage >= 0 && game.card1) {
    cardEntries.push(
      { card: game.card1, stage: 0 },
      { card: game.card2, stage: 0 },
      { card: game.card3, stage: 0 }
    );
  }
  if (maxStage >= 1 && game.card4) cardEntries.push({ card: game.card4, stage: 1 });
  if (maxStage >= 2 && game.card5) cardEntries.push({ card: game.card5, stage: 2 });

  // Parse first, THEN filter. The wire carries non-card sentinels in these
  // slots — 'facedown' (masked), '0'/'' (cleared) — and can in principle carry
  // any unparseable string (e.g. a ciphertext that failed decryption
  // server-side). mapToCard returns null for all of those, and the old
  // truthiness filter let them through, where `{ ...null }` produced a
  // faceless card object that CommunityCard painted as a stuck card back.
  // A slot without a real card now renders as the dashed placeholder — the
  // truthful state, and what Unity shows (it hides face-down board cards).
  const rawCards = cardEntries
    .map((c) => ({ mapped: mapToCard(c.card), card: c.card, stage: c.stage }))
    .filter((c) => !!c.mapped)
    .map((c) => ({ ...c.mapped, card: c.card, stage: c.stage }));

  // Suppress stale cards: after a sweep the engine leaves old card values in the
  // DB, so the new hand's hand='6' broadcast can carry the previous flop.
  // Don't render until the fingerprint changes (genuinely new cards arrived).
  const currentFingerprint = rawCards.map((c) => c.card).join(',');
  currentFingerprintRef.current = currentFingerprint;
  const isStaleCards =
    sweptFingerprintRef.current !== '' &&
    currentFingerprint !== '' &&
    currentFingerprint === sweptFingerprintRef.current;
  const cards = isStaleCards ? [] : rawCards;

  // Track card count to determine which cards are newly dealt
  const prevCardCountRef = useRef(0);
  const newCardStartIndex = prevCardCountRef.current;

  // Stabilize card keys — once a card is shown, keep its original string
  // so React doesn't remount/re-flip if the engine resends updated values
  const stableCardKeysRef = useRef<string[]>([]);

  // Only reset refs on a genuine new-hand boundary (see isNewHandBoundary
  // above), not when cards temporarily empty at game close
  if (isNewHandBoundary) {
    stableCardKeysRef.current = [];
    prevCardCountRef.current = 0;
  }

  cards.forEach((c, i) => {
    if (!stableCardKeysRef.current[i]) {
      stableCardKeysRef.current[i] = c.card;
    }
  });

  useEffect(() => {
    // Only track increases — don't reset to 0 when cards empty at game close
    if (cards.length > prevCardCountRef.current) {
      prevCardCountRef.current = cards.length;
    }
  }, [cards.length]);

  // Track previous cards for sweep-out animation
  useEffect(() => {
    if (cards.length > 0) {
      setPrevCards(cards);
    }
  }, [cards.length]);

  // Live play must NOT re-run the reset effect on a fingerprint change: the
  // sweep itself empties the fingerprint (maxStageRef = -1), which would start
  // a second sweep and overwrite sweptFingerprintRef with '' — disabling the
  // stale-flop guard. Only snapshot mode needs to follow the board's content.
  const snapshotFingerprint = isHandHistorySnapshot ? currentFingerprint : '';

  // Detect hand reset — animate out then hide
  useEffect(() => {
    const prevNum = +prevHandRef.current;
    const currNum = +hand;
    prevHandRef.current = hand;

    if (isHandHistorySnapshot) {
      setIsVisible(true);
      if (cards.length > 0) setPrevCards(cards);
      if (cardsContainerRef.current) {
        gsap.set(cardsContainerRef.current, { opacity: 1, y: 0, scale: 1 });
      }
      return;
    }

    // hand=14 sweeps ONLY on an all-fold (GAME_MSG_ALLFOLD); a real showdown
    // keeps the board through the winner reveal — see shouldSweepBoard.

    // Missed sweep: the hand number went BACKWARDS without passing through the
    // sweep states (''/0/1) — the client was backgrounded or lobby-browsing
    // across the transition and the first payload it applies belongs to the
    // NEXT hand (typically hand 5, blinds/dealing). The sweep moment is long
    // past, so clear immediately instead of animating: without this, prevCards
    // kept the PREVIOUS hand's board on the felt into the new hand
    // (docs/investigations/20260821-community-cards-facedown-after-resume.md).
    const isMissedHandReset =
      currNum < prevNum && currNum < 6 && !shouldSweepBoard(currNum, hand, isAllFold);
    if (isMissedHandReset) {
      sweptFingerprintRef.current = currentFingerprintRef.current;
      maxStageRef.current = -1;
      setIsVisible(false);
      setPrevCards([]);
    } else if (shouldSweepBoard(currNum, hand, isAllFold)) {
      // Hand reset or all-fold winner (Unity: PayWinners=14) — clear community cards.
      // Mirror Unity's ClearCardValue() client-side override: the server doesn't clear
      // card1..card5 at hand='14', so we do it explicitly here.
      sweptFingerprintRef.current = currentFingerprintRef.current;
      maxStageRef.current = -1; // Prevent stale cards rendering before next genuine deal
      if (prevNum >= 6 && cardsContainerRef.current) {
        // Snapping (arrival / resync): the sweep already happened — clear the
        // board immediately instead of replaying it.
        snapOrTo(shouldSnap(), cardsContainerRef.current, {
          opacity: 0,
          y: -30,
          scale: 0.8,
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            setIsVisible(false);
            setPrevCards([]);
            // Reset for next hand
            if (cardsContainerRef.current) {
              gsap.set(cardsContainerRef.current, { opacity: 1, y: 0, scale: 1 });
            }
          }
        });
      } else {
        // No cards were dealt, or ref unavailable — clear immediately
        setIsVisible(false);
        setPrevCards([]);
      }
    } else if (currNum >= 4 && currNum < 14 && !isVisible) {
      // New hand starting — show again
      setIsVisible(true);
      if (cardsContainerRef.current) {
        snapOrFromTo(
          shouldSnap(),
          cardsContainerRef.current,
          { opacity: 0, y: 15, scale: 0.9 },
          { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: 'power2.out' }
        );
      }
    }
    // `snapshotFingerprint` (a stable string), not `cards` (a new array
    // reference every render): in hand-history snapshot mode this effect
    // unconditionally calls setPrevCards(cards), and a `cards`-array dependency
    // never compares equal across renders, so every commit re-fires the effect,
    // which calls setState again, forever — an infinite render loop that hangs
    // the tab on any hand that reached the flop or later.
  }, [snapshotFingerprint, hand, isAllFold, isHandHistorySnapshot]);

  // Mirror Unity UITableCards: alpha=0 when GAME_MSG_PROCESSING (hand='15' transition).
  // The server sends this key during the inter-hand reset; Unity hides community cards
  // instantly at this point regardless of card1..card5 values.
  const displayCards = isProcessing ? [] : cards.length > 0 ? cards : isVisible ? prevCards : [];

  // EN-11031: report, and answer on the felt, a dealt street the board is not
  // showing (see boardDeficit).
  const boardDeficit = useBoardDeficit({
    enabled: !isHandHistorySnapshot && !showMultiRun,
    handNum,
    maxStage,
    shownCards: displayCards.length,
    isVisible,
    isStaleCards,
    game,
    playerGUID
  });
  // A slot the street is owed a card for and has none: marked, never covered.
  const isMissingSlot = (i: number) =>
    boardDeficit.state !== 'none' && !displayCards[i] && i < boardDeficit.expected;

  return (
    // Board scale lives on this wrapper, never on the GSAP-animated card row:
    // the deal/sweep tweens write `scale` directly to the row and would clobber
    // a static scale there (resetting it to 1 leaves the cards full-size after a
    // hand reset).
    //
    // The board tracks `--seat-scale` UPWARD ONLY.
    //
    // Portrait used to be a bare `scale(0.75)` that ignored the container, and
    // making it track faithfully was a mistake: every multi-table grid cell is
    // forced portrait (MultiTableGrid's OrientationOverrideProvider) and runs a
    // seat scale well under 1, so the board fell from a readable 39px to ~29px.
    // The grid got WORSE, which is the opposite of the point.
    //
    // That bare constant was doing real work — holding a legibility floor for
    // the community cards on a small felt — so it is kept as a floor and the
    // scale only ever grows it. See BOARD_MIN_SCALE_FACTOR.
    <div
      style={{
        transform: `scale(${boardScale})`,
        transformOrigin: 'center center',
        // A scaled transform doesn't shrink the layout box, so a small board
        // reserves full size and opens a gap to the details (vertical) and the
        // side pots (horizontal). Reclaim that slack on both axes with a negative
        // margin = (scale - 1) * halfSize, so the footprint tracks the visuals.
        margin: `calc((${boardScale} - 1) * ${boardHalfHeight}px) calc((${boardScale} - 1) * ${boardHalfWidth}px)`,
        display: 'flex',
        justifyContent: 'center'
      }}>
      <div style={communityContainerStyle} ref={holdemRef}>
        {showMultiRun ? (
          <MultiRunBoards runKeys={runKeys} cardGap={cardGap} />
        ) : (
          /* Five fixed board SLOTS. They still reserve the full board footprint
             so the felt never reflows as the flop/turn/river land — but EN-10520
             drops their dashed outline: five dashed rectangles sitting across the
             centre watermark made the felt read as busy and cut the logo in half
             pre-flop. Player seats KEEP their outlines (Seat/styles
             cardOutlineStyle), where they usefully say "your cards land here". */
          <div
            ref={cardsContainerRef}
            style={{
              ...CardsRowContainerStyle,
              gap: cardGap
            }}>
            {_.range(5).map((i) => (
              <div
                key={`placeholder-${i}`}
                {...(isMissingSlot(i)
                  ? { style: missingBoardSlotStyle, className: missingSlotClassName }
                  : { style: boardSlotStyle })}>
                {/* Actual card overlaid on placeholder */}
                {displayCards[i] && (
                  <CardReveal
                    key={`card-${i}-${stableCardKeysRef.current[i] || displayCards[i].card}`}
                    index={i}>
                    <CommunityCard
                      suit={displayCards[i].suit}
                      rank={displayCards[i].rank}
                      card={displayCards[i].card}
                      skipFlip={i < newCardStartIndex || handNum >= 13}
                    />
                  </CardReveal>
                )}
              </div>
            ))}
          </div>
        )}
        {boardDeficit.state !== 'none' && (
          <BoardMissingNotice state={boardDeficit.state} onReconnect={boardDeficit.reconnect} />
        )}
      </div>
    </div>
  );
};

// The shared community-card prefix (dealt once, before the all-in) is not
// re-dealt per run — RunMultiple only re-deals from `fixedCards` onward. The
// hand stage in `runs` (index 1) tells us how many cards are shared, so we can
// draw them once and fork only the run-specific tail (matching the classic
// run-it-twice branch layout). Mirrors RunMultiple's `fixedCards` switch.
/**
 * Courchevel — the first flop card is exposed before pre-flop betting.
 *
 * Mirrors `isCourchevel` in serverless-v2/shared/games/common/gameType.js. Kept
 * local because this client is a separate build with no import path into `shared`;
 * NULL-safe for the same reason that module is — `gameType` is nullable on both
 * game tables and reaches the client as '' or undefined on those rows.
 */
export const isCourchevelType = (gameType?: string): boolean =>
  String(gameType ?? '').includes('CL');

export const sharedCardCount = (stage: string, gameType?: string): number => {
  switch (stage) {
    case '5':
      // Courchevel's exposed card is shared information every player has already
      // acted on, so RunMultiple keeps it across the re-deal (`fixedCards = 1`).
      // Without this case the client treated the completed run as having no shared
      // prefix and rendered a 4-card board with the exposed card missing.
      return isCourchevelType(gameType) ? 1 : 0;
    case '6':
    case '7':
      return 3; // flop shared, turn + river re-run
    case '8':
    case '9':
      return 4; // flop + turn shared, river re-run
    default:
      return 0; // all-in pre-flop — every card differs per run
  }
};

/**
 * Run-it-twice board render: the shared community cards are drawn once, then the
 * board forks into each run's divergent cards (turn+river, or just the river).
 * The run(s) the current player won are accented. Prior runs are already
 * resolved, so cards are shown face-up (skipFlip) rather than re-dealt.
 *
 * Boards carry no labels of their own: per-run pots and "who took which pot"
 * made the felt unreadable, and the hand history already holds all of it. The
 * only figures on the felt are the Pot's own pills, which line themselves up
 * with the board being run now (see Pot.tsx).
 */
/**
 * What one branch of the fork IS, in words.
 *
 * Boards are pre-dealt (AC-6.3), so three states share the felt during the
 * payout sequence: the board being run now, boards already paid, and boards
 * dealt but still to come. On screen the last two differ ONLY by the dim — and
 * AC-7.2 rules out inventing a visible treatment to separate them, so the
 * distinction is carried here instead, where it costs the felt nothing. Without
 * it the payout order is conveyed by brightness alone: unreadable to a screen
 * reader, and marginal on a sunlit phone.
 */
export const branchLabel = (
  runKey: string,
  isSettledRun: boolean,
  isPastRun: boolean,
  currentPlayerWon: boolean,
  isDealt = true
): string => {
  const run = `Run ${runKey.slice(3)}`;
  const won = currentPlayerWon ? ', you won this board' : '';
  // The row is held from the first board on (see getRunKeys), so it can exist
  // before it has any cards. Calling that "dealt" would mislead the one reader
  // who cannot see the empty row for themselves.
  if (!isDealt) return `${run}, not dealt yet`;
  if (isSettledRun) return `${run}, paid${won}`;
  // Not settled and not the live board — dealt, waiting its turn to be paid.
  if (isPastRun) return `${run}, dealt, not yet paid`;
  return `${run}, being run now${won}`;
};

const MultiRunBoards = ({ runKeys, cardGap }: { runKeys: string[]; cardGap: number }) => {
  const multiRunCards = useTableSelector((table) => table.game.multiRunCards);
  const runWinners = useTableSelector((table) => table.game.runWinners);
  const players = useTableSelector((table) => table.players);
  const currentSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const runs = useTableSelector((table) => table.game.runs);
  // Needed for the stage-'5' shared-prefix rule: Courchevel keeps its exposed
  // card across the re-deal, other variants share nothing pre-flop.
  const gameType = useTableSelector((table) => table.game.gameType);
  // Portrait has far less horizontal room, so the fork tightens its connectors
  // to keep the whole diagram on the felt without clipping.
  const isPortrait = useOrientation() === 'portrait';

  // The runs with cards of their own. The rest are rows held for boards still
  // to come (see getRunKeys), and only this says which is which — a run's tail
  // can be empty while its board IS on the felt, when the whole shared flop is
  // all that has been dealt.
  const dealtKeys = getRunKeys(multiRunCards);

  // runs = "<n>,<stage>,<currentRun>" while boards are running.
  const runsArray = (runs ?? '').split(',');
  const currentRun = +(runsArray[2] ?? '1');
  const sharedCount = sharedCardCount(runsArray[1] ?? '', gameType);

  // The in-progress run carries the full live board (shared prefix + its tail);
  // completed runs are stored as the divergent tail only (see broadcaster). Take
  // the shared prefix from the live board so it's drawn exactly once.
  const liveBoard = (multiRunCards?.[`run${currentRun}`] as string[]) ?? [];
  const sharedCards = liveBoard
    .slice(0, sharedCount)
    .filter(Boolean)
    .map((c) => ({ ...mapToCard(c), card: c }));

  // How many cards each branch (run-specific tail) should reserve a slot for.
  const branchLength = Math.max(1, 5 - sharedCount);

  const mapTail = (runKey: string) => {
    const arr = (multiRunCards?.[runKey] as string[]) ?? [];
    // The current run holds the full board — strip the shared prefix; completed
    // runs are already stored tail-only.
    const tail = runKey === `run${currentRun}` ? arr.slice(sharedCount) : arr;
    return tail.filter(Boolean).map((c) => ({ ...mapToCard(c), card: c }));
  };

  return (
    <div style={multiRunForkStyle}>
      {sharedCards.length > 0 && (
        <>
          <div style={{ ...CardsRowContainerStyle, gap: cardGap }}>
            {sharedCards.map((c, i) => (
              <div key={`shared-${i}`} style={boardSlotStyle}>
                {c.suit && c.rank && (
                  <div style={cardOverlayStyle}>
                    <CommunityCard suit={c.suit} rank={c.rank} card={c.card} skipFlip />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Stem from the shared cards to the fork trunk */}
          <div style={{ ...forkStemStyle, width: isPortrait ? 6 : 12 }} />
        </>
      )}

      <div style={branchStackStyle}>
        {runKeys.map((runKey, runIndex) => {
          const tail = mapTail(runKey);
          // Boards before the one being run now are settled history, and dim.
          // The live `pots`/`showdown` that the highlight reads describe only
          // the current board, so matching an earlier board's cards against
          // them leaves the previous run's winning hand lit through this run's
          // showdown. Gated on `currentRun` naming a real run: a `runs` string
          // whose run segment is missing or unparseable (`"2,7,"` → 0) would
          // otherwise mark EVERY branch past and dim the live board with them.
          const isPastRun = currentRun >= 1 && runKey !== `run${currentRun}`;
          // Whether that board has already PAID OUT, as opposed to being
          // pre-dealt and still to come (AC-6.3 puts every run-out on the felt
          // before payout 1). Both are dim — only the run being paid is lit — so
          // this separates the two for the aria label alone.
          const isSettledRun = currentRun >= 1 && +runKey.slice(3) < currentRun;

          const runData: any = runWinners?.[runKey] ?? {};
          const currentPlayerWon = seatWonRun(runWinnerList(runData, players), currentSeatId);

          return (
            <div
              key={runKey}
              style={branchRowStyle}
              // The winner banner finds its row by this attribute rather than by
              // counting row heights: only the row's own box knows where it
              // painted. See TableWinnerMessage.
              data-run-row={runKey}
              role='group'
              aria-label={branchLabel(
                runKey,
                isSettledRun,
                isPastRun,
                currentPlayerWon,
                dealtKeys.includes(runKey)
              )}>
              {/* Tree connector: horizontal stub + vertical trunk segment */}
              <div style={{ ...connectorCellStyle, width: isPortrait ? 12 : 18 }}>
                <div style={connectorHorizontalStyle} />
                {runKeys.length > 1 && (
                  <div style={connectorVerticalStyle(runIndex, runKeys.length)} />
                )}
              </div>

              <div
                style={{
                  ...branchCardsWrapStyle,
                  ...(currentPlayerWon ? branchWonStyle : {})
                }}>
                <div
                  style={{
                    ...CardsRowContainerStyle,
                    gap: cardGap
                  }}>
                  {_.range(branchLength).map((i) => {
                    const c = tail[i];
                    return (
                      <div key={`${runKey}-ph-${i}`} style={boardSlotStyle}>
                        {c?.suit && c?.rank && (
                          <div style={cardOverlayStyle}>
                            <CommunityCard
                              suit={c.suit}
                              rank={c.rank}
                              card={c.card}
                              skipFlip
                              isPastRun={isPastRun}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HoldEmCards;

// Vertical gap between branches, applied as row padding (not flex gap) so the
// tree trunk stays continuous across the boundary between adjacent branches.
const BRANCH_GAP = 10;
// The board the viewer took. This sits ON the felt, so it cannot reuse the
// seat's green winner colour (#628B48 in SeatWithPlayer) — that reads against
// the dark chrome behind a seat, but green-on-green vanished on the table and
// the highlight looked broken. Gold is the table's existing "this is yours"
// accent (the POV seat ring, the dealer button), and it separates from the felt.
const WON_ACCENT = 'rgba(212, 168, 83, 0.95)';
const WON_GLOW = 'rgba(212, 168, 83, 0.55)';
const TRUNK_COLOR = 'rgba(255, 255, 255, 0.25)';

// Shared cards | stem | branch stack — laid out left-to-right so the board
// forks like the classic run-it-twice diagram.
const multiRunForkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: ZIndex.card
};

// Short horizontal line joining the shared cards to the fork trunk.
const forkStemStyle: CSSProperties = {
  width: 12,
  height: 0,
  borderTop: `2px solid ${TRUNK_COLOR}`
};

const branchStackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column'
};

// Cards vertically centred; BRANCH_GAP as padding keeps the trunk unbroken.
const branchRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  paddingTop: BRANCH_GAP / 2,
  paddingBottom: BRANCH_GAP / 2
};

// Fixed-width cell holding the tree connector for one branch. Stretches to the
// full row height so its 50% mark aligns with the (centred) cards.
const connectorCellStyle: CSSProperties = {
  position: 'relative',
  alignSelf: 'stretch',
  width: 18,
  flexShrink: 0
};

const connectorHorizontalStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 'calc(50% - 1px)',
  height: 2,
  backgroundColor: TRUNK_COLOR
};

// Vertical trunk segment: the first branch draws from its centre down, the last
// from the top to its centre, middles span the full height. With row padding
// (not a flex gap) the segments meet at the row boundaries into one line.
const connectorVerticalStyle = (index: number, count: number): CSSProperties => {
  const isFirst = index === 0;
  const isLast = index === count - 1;
  return {
    position: 'absolute',
    left: 0,
    width: 2,
    backgroundColor: TRUNK_COLOR,
    top: isFirst ? 'calc(50% - 1px)' : 0,
    bottom: isLast ? 'calc(50% - 1px)' : 0
  };
};

const branchCardsWrapStyle: CSSProperties = {
  display: 'flex',
  padding: 4,
  borderRadius: 8,
  border: '2px solid transparent',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
};

const branchWonStyle: CSSProperties = {
  border: `2px solid ${WON_ACCENT}`,
  boxShadow: `0 0 14px ${WON_GLOW}`,
  // Darkened rather than tinted — a light wash over green felt is barely a
  // change, while a slight darkening makes the ring read as a distinct panel.
  backgroundColor: 'rgba(0, 0, 0, 0.25)'
};

const communityContainerStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  zIndex: ZIndex.card
};

// A board slot: reserves a card's footprint, paints nothing. The dashed outline
// this used to carry is gone (EN-10520) — see the render for why. Exported so a
// unit test can hold the line against it coming back.
export const boardSlotStyle: CSSProperties = {
  position: 'relative',
  // Plain px: the slot reserves the card's DESIGN footprint and the wrapper's
  // scale() sizes it, so the reserved box and the painted card track each other
  // at every Table View Size. See CommunityCard's BaseStyle.
  width: `${SMALL_CARD_WIDTH}px`,
  height: `${SMALL_CARD_HEIGHT}px`,
  boxSizing: 'border-box',
  borderRadius: '0.25rem',
  display: 'block',
  border: 'none',
  zIndex: 2
};

// A slot whose dealt card is missing (EN-11031): the same footprint, with the
// inline `border: none` dropped so missingSlotClassName can draw its outline.
const missingBoardSlotStyle: CSSProperties = _.omit(boardSlotStyle, 'border');

const cardOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 3
};

const CardsRowContainerStyle: CSSProperties = {
  display: 'flex',
  gap: 6,
  zIndex: ZIndex.card
};
